import { z } from "zod";
import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { BucketIcon, LockIcon, UserIcon } from "@/shared/icons";
import { Button, Modal, TextInput } from "@/shared/ui";

const nameSchema = (value: string) => {
  return z
    .string()
    .nonempty("Имя не должно быть пустым")
    .refine((name) => name === value, {
      message: "Имя не совпадает",
    });
};

type Props = {
  characterName: string;
  open: boolean;
  onSubmit: (password: string) => void;
  onCancel: () => void;
  pending: boolean;
  error?: string;
};

export default function AccountCharDeleteConfirmName({
  characterName,
  open,
  onSubmit,
  onCancel,
  pending,
  error,
}: Props) {
  const [nameError, setNameError] = useState("");
  const [touch, setTouch] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const toogleShowPassword = useCallback(() => {
    setShowPassword((state) => !state);
  }, []);

  useEffect(() => {
    if (!open) {
      setName("");
      setPassword("");
      setNameError("");
      setTouch(false);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onCancel} className="w-[27rem]" disableAnimation>
      <div className="mb-5 font-bold text-center">
        Для удаления персонажа введите полные имя и фамилию персонажа, а также ваш пароль
      </div>
      <form
        className="flex flex-col w-full justify-center gap-3.5"
        noValidate
        onSubmit={(e) => {
          setTouch(true);
          e.preventDefault();
          if (!name || !password) return;
          try {
            setNameError("");
            nameSchema(characterName).parse(name);
            onSubmit(password);
          } catch (err) {
            if (err instanceof z.ZodError) {
              setNameError(err.errors[0].message);
            }
          }
        }}
      >
        <TextInput
          type="text"
          placeholder="Имя и фамилия персонажа"
          title="Имя и фамилия:"
          icon={<UserIcon size={16} />}
          invalid={touch && !name ? "Введите имя и фамилию" : ""}
          value={name}
          onChange={setName}
          required
          className="text-center [&_.control-status]:absolute"
        />
        <TextInput
          type="password"
          placeholder="Введите пароль"
          title="Пароль:"
          icon={<LockIcon size={16} />}
          invalid={touch && !password ? "Введите пароль" : ""}
          value={password}
          onChange={setPassword}
          required
          className="text-center [&_.control-status]:absolute"
          passwordVisible={showPassword}
          onChangePasswordVisible={toogleShowPassword}
        />
        <div className="mt-auto grid gap-2.5">
          <div
            className={clsx("alert alert--danger", nameError || error ? "visible" : "invisible")}
          >
            *{nameError || error}
          </div>
          <div
            className={clsx(
              "flex items-center justify-center",
              "w-full bg-gradient-to-r from-[#FF7958]/15 to-[#FF7958]/[.07] text-[#FF9E58]",
              "text-14 font-medium py-2.5 rounded-[0.5rem] px-3",
            )}
          >
            Внимание! Удаление персонажа занимает 14 дней
          </div>
          <Button submit loading={pending} disabled={pending} className="mt-1.5">
            <div className="flex items-center">
              <BucketIcon size={16} className="mr-2.5" />
              Удалить персонажа
            </div>
          </Button>
        </div>
      </form>
    </Modal>
  );
}
